import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Alert,
  Heading, Spinner,
} from '@printdeal/ui-library';
import { usePrefetchAddresses } from '@printdeal/address-frontend';
import { Address } from '@printdeal/address-frontend/types';
import Breadcrumb from '../../../../Breadcrumb';
import Head from '../../../../PrivateHead';
import useGetChangeAscriptionDataQuery from './queries/useGetChangeAscriptionDataQuery';
import useCreateChangeAscriptionMutation from './queries/useCreateChangeAscriptionMutation';
import ChangeAscriptionForm from './form/ChangeAscriptionForm';

interface Props {
  invoiceNumber: string;
}

const ChangeAscriptionPage = ({ invoiceNumber }: Props) => {
  usePrefetchAddresses();

  const intl = useIntl();
  const changeAscriptionQuery = useGetChangeAscriptionDataQuery({ invoiceNumber });
  const changeAscriptionMutation = useCreateChangeAscriptionMutation({ invoiceNumber });

  return (
    <div className="pd-flex pd-flex-col pd-gap-3">
      <Head title={intl.formatMessage({ id: 'account.invoices.list.change.ascription.button', defaultMessage: 'Change ascription' })} />
      <Breadcrumb>
        <Breadcrumb.Item to="../../..">
          <FormattedMessage id="account.index.title" defaultMessage="My account" />
        </Breadcrumb.Item>
        <Breadcrumb.Item to="../..">
          <FormattedMessage id="account.invoices.page_title" defaultMessage="My invoices" />
        </Breadcrumb.Item>
        <Breadcrumb.Item isCurrentPage>
          <FormattedMessage id="account.invoices.list.change.ascription.button" defaultMessage="Change ascription" />
        </Breadcrumb.Item>
      </Breadcrumb>

      <Heading as="h2">
        <FormattedMessage
          id="account.invoices.changeAscription.title"
          defaultMessage="Change ascription {invoiceNumber}"
          values={{ invoiceNumber }}
        />
      </Heading>

      {changeAscriptionMutation.isError && (
        <Alert variant="error">
          <FormattedMessage id="account.invoices.list.change.ascription.error" defaultMessage="Change Ascription Error" />
        </Alert>
      )}

      {changeAscriptionQuery.data ? (
        <ChangeAscriptionForm
          invoiceNumber={invoiceNumber}
          defaultValues={{
            billingAddress: Object.fromEntries(
              Object.entries(changeAscriptionQuery.data.billingAddress || {}).filter(([, value]) => value !== ''),
            ) as Address,
            vatId: changeAscriptionQuery.data?.vatNumber || '',
            poNumber: changeAscriptionQuery.data?.poNumber || '',
            reference: changeAscriptionQuery.data?.reference || '',
          }}
          changeAscriptionMutation={changeAscriptionMutation}
        />
      ) : (
        <>
          {changeAscriptionQuery.isLoading && (
            <div className="pd-flex pd-justify-center pd-p-10">
              <Spinner />
            </div>
          )}
          {changeAscriptionQuery.isError && (
            <Alert variant="error">
              <FormattedMessage
                id="account.invoices.ascription.error"
                defaultMessage="An error occurred while loading the ascription data. Please try again later."
              />
            </Alert>
          )}
        </>
      )}
    </div>
  );
};

export default ChangeAscriptionPage;
